import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { qk, type AgentThread, type AgentTurn } from '@immoaktas/api-client';
import { AGENT_LIMITS, agentFileType, agentSection, type AgentProposalDto } from '@immoaktas/shared';
import { ApiError } from '@immoaktas/api-client';
import { api } from '../lib/api';
import { usePropertyScope } from '../shell/useScope';

/**
 * State of the KI-Assistent panel (docs/17 §Oberfläche): open/closed per browser (localStorage), hints hidden for the
 * open chat only (sessionStorage, back with the next chat),
 * the chat in react-query (server is the source of truth), files attached to the composer and the batch being sent
 * (shown at once like in the chat with Claude, with upload progress). Nothing is read before "Senden".
 * The open chat lives for the browser session only (sessionStorage): a new session or app start begins with an empty
 * chat, earlier chats are in the history (docs/17 §Verlauf).
 */
export type Attached = { id: string; file: File; error: string | null };
export type OutgoingFile = { id: string; name: string; size: number; phase: 'uploading' | 'failed'; error?: string };
export type Outgoing = { id: string; text: string; files: OutgoingFile[]; failed: boolean };

const OPEN_KEY = 'ia:agent:open';
const HIDDEN_KEY = 'ia:agent:hidden';
const CHAT_KEY = 'ia:agent:conversation';
const readSession = () => {
  try {
    const v = sessionStorage.getItem(CHAT_KEY);
    return v && /^[a-f0-9]{24}$/.test(v) ? v : null;
  } catch {
    return null;
  }
};
const writeSession = (id: string | null) => {
  try {
    if (id) sessionStorage.setItem(CHAT_KEY, id);
    else sessionStorage.removeItem(CHAT_KEY);
  } catch {
    /* storage blocked: the chat stays for this page only */
  }
};
const readHidden = (): string[] => {
  try {
    const v = JSON.parse(sessionStorage.getItem(HIDDEN_KEY) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};
const writeHidden = (ids: string[]) => {
  try {
    if (ids.length) sessionStorage.setItem(HIDDEN_KEY, JSON.stringify(ids));
    else sessionStorage.removeItem(HIDDEN_KEY);
  } catch {
    /* storage blocked: hidden for this page only */
  }
};
/** From this many messages on, a resumed chat shows the hint to start a new one (docs/17 §Kosten). */
export const LONG_CHAT_MESSAGES = 30;
/** Why a send did not go out: no Premium (trial used up, 402) or the budget of the month/day is used up. */
export type Blocker = 'premium' | 'limit' | 'limit_month';
const readLocal = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const writeLocal = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* storage blocked: remembered for this tab only */
  }
};
const wide = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1280px)').matches;

type UiState = {
  open: boolean;
  /** The open chat of this browser session; null = a new, empty chat. */
  conversationId: string | null;
  /** Shown instead of sending (Premium offer or the limit note); the typed text stays. */
  blocker: Blocker | null;
  attached: Attached[];
  outgoing: Outgoing | null;
  busy: boolean;
  /** Tips, hints and the case card hidden in the open chat; a new or another chat shows them again. */
  hiddenTips: string[];
  /** Set by the open panel: continue the chat after a proposal of it was applied (docs/adr/0013). */
  afterApply: ((p: AgentProposalDto) => void) | null;
  setOpen: (open: boolean) => void;
  hideTip: (id: string) => void;
  set: (p: Partial<UiState>) => void;
};
export const useAgentUi = create<UiState>((set, get) => ({
  // first visit on a wide screen: open, so the landlord sees the assistant once; afterwards the last choice
  open: readLocal(OPEN_KEY) ? readLocal(OPEN_KEY) === '1' : wide(),
  conversationId: readSession(),
  blocker: null,
  attached: [],
  outgoing: null,
  busy: false,
  afterApply: null,
  hiddenTips: readHidden(),
  setOpen: (open) => {
    writeLocal(OPEN_KEY, open ? '1' : '0');
    set({ open });
  },
  hideTip: (id) => {
    const hiddenTips = [...new Set([...get().hiddenTips, id])].slice(-200);
    writeHidden(hiddenTips);
    set({ hiddenTips });
  },
  set: (p) => set(p),
}));
useAgentUi.subscribe((s, prev) => {
  if (s.conversationId !== prev.conversationId) writeSession(s.conversationId);
});

const rid = () => Math.random().toString(36).slice(2, 10);

/** File → base64 without the data: prefix. */
export function toBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error ?? new Error('read failed'));
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.readAsDataURL(file);
  });
}

/** Everything a proposal may have changed: refetch all data queries except the assistant's own. */
export const refreshData = (qc: QueryClient) => qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'agent' });

function mergeTurn(old: AgentThread | undefined, turn: AgentTurn): AgentThread {
  const proposals = new Map((old?.proposals ?? []).map((p) => [p.id, p]));
  for (const p of turn.proposals) proposals.set(p.id, p);
  const seen = new Set((old?.messages ?? []).map((m) => m.id));
  return {
    conversation: { id: turn.conversationId, title: old?.conversation?.title ?? '', lastMessageAt: new Date().toISOString() },
    messages: [...(old?.messages ?? []), ...turn.messages.filter((m) => !seen.has(m.id))],
    proposals: [...proposals.values()],
  };
}
export function upsertProposal(qc: QueryClient, p: AgentProposalDto) {
  qc.setQueryData<AgentThread>(qk.agent.thread, (old) => (old ? { ...old, proposals: old.proposals.some((x) => x.id === p.id) ? old.proposals.map((x) => (x.id === p.id ? p : x)) : [...old.proposals, p] } : old));
}

export function useAgentStatus() {
  return useQuery({ queryKey: qk.agent.status, queryFn: () => api.agent.status(), staleTime: 60_000 });
}

export function useAgent() {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const scope = usePropertyScope();
  const ui = useAgentUi();
  const status = useAgentStatus();
  const ready = !!status.data?.consent.granted && status.data.configured && status.data.enabled;
  // the composer is open whenever the assistant is usable; whether a message may cost money is decided on "Senden":
  // without Premium/override/trial the Premium offer shows, with the budget used up the limit note (docs/17 §Premium)
  const canSend = ready;
  const blockerNow = (): Blocker | null => {
    const s = status.data;
    if (!s) return null;
    if (!s.access?.via) return 'premium';
    if (s.budget?.exhausted) return s.budget.exhausted === 'month' ? 'limit_month' : 'limit';
    return null;
  };
  const thread = useQuery({
    queryKey: qk.agent.thread,
    queryFn: async (): Promise<AgentThread> => {
      const id = useAgentUi.getState().conversationId;
      if (!id) return { conversation: null, messages: [], proposals: [] };
      try {
        return await api.agent.conversation(id);
      } catch (e) {
        // the chat was deleted (elsewhere) or belongs to another login: start a new one instead of an error
        if (e instanceof ApiError && e.status === 404) {
          useAgentUi.getState().set({ conversationId: null });
          return { conversation: null, messages: [], proposals: [] };
        }
        throw e;
      }
    },
    enabled: ready && ui.open,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
  const tipsOn = status.data?.settings.tips !== false;
  const tips = useQuery({
    queryKey: qk.agent.tips(pathname, scope.propertyId ?? undefined),
    queryFn: () => api.agent.tips(pathname, scope.propertyId ?? undefined),
    enabled: ui.open && tipsOn && !!status.data,
    staleTime: 120_000,
  });
  // the entity in focus (unit pages, a tenancy inside its unit): the assistant greets and answers for exactly that
  const unitId = scope.path.kind === 'unit' ? scope.path.unitId : undefined;
  const tenancyId = scope.path.kind === 'unit' ? scope.path.tenancyId : scope.path.kind === 'entity' && scope.path.entity === 'tenancy' ? scope.path.id : undefined;
  const page = {
    path: pathname,
    ...(scope.propertyId ? { propertyId: scope.propertyId } : {}),
    ...(agentSection(pathname) ? { section: agentSection(pathname)! } : {}),
    ...(unitId ? { unitId } : {}),
    ...(tenancyId ? { tenancyId } : {}),
  };
  const guideQ = { path: pathname, ...(page.propertyId ? { propertyId: page.propertyId } : {}), ...(unitId ? { unitId } : {}), ...(tenancyId ? { tenancyId } : {}), ...(ui.conversationId ? { conversationId: ui.conversationId } : {}) };
  // greeting, case checklist and next steps: rules on the server, no AI cost (docs/17 §Steuerung)
  const guide = useQuery({
    queryKey: qk.agent.guide(guideQ),
    queryFn: () => api.agent.guide(guideQ),
    enabled: ready && ui.open,
    staleTime: 60_000,
    retry: false,
    // a new chat id or page keeps the last card until the new one is there (no flicker)
    placeholderData: keepPreviousData,
  });

  /** Adds files to the composer (checked by name and size; content is checked by the server). Returns files left out. */
  const attach = useCallback((files: File[], texts: { tooLarge: (n: string) => string; badType: (n: string) => string }) => {
    const cur = useAgentUi.getState().attached;
    const room = Math.max(0, AGENT_LIMITS.filesPerMessage - cur.length);
    const next: Attached[] = files.slice(0, room).map((file) => ({
      id: rid(),
      file,
      error: !agentFileType(file.name) ? texts.badType(file.name) : file.size > AGENT_LIMITS.fileMaxBytes ? texts.tooLarge(file.name) : null,
    }));
    useAgentUi.getState().set({ attached: [...cur, ...next] });
    return files.length - next.length;
  }, []);

  /**
   * Sends text, files or an answer. Returns false without any request when the account may not send (no Premium or
   * the budget is used up): the panel then shows the Premium offer or the limit note and keeps the text.
   */
  const send = useCallback(
    async (text: string, opts: { answer?: { messageId: string; choice: string; skip?: boolean } } = {}): Promise<boolean> => {
      const st = useAgentUi.getState();
      if (st.busy) return false;
      const blocked = blockerNow();
      if (blocked) {
        st.set({ blocker: blocked });
        return false;
      }
      const files = st.attached.filter((a) => !a.error);
      const out: Outgoing = { id: rid(), text: opts.answer && !text ? opts.answer.choice : text, files: files.map((f) => ({ id: f.id, name: f.file.name, size: f.file.size, phase: 'uploading' })), failed: false };
      st.set({ busy: true, outgoing: out, attached: [] });
      try {
        const ids: string[] = [];
        for (const f of files) {
          try {
            const a = await api.agent.attach({ name: f.file.name, size: f.file.size, dataBase64: await toBase64(f.file), ...(st.conversationId ? { conversationId: st.conversationId } : {}) });
            ids.push(a.id);
          } catch (e) {
            const cur = useAgentUi.getState().outgoing;
            if (cur) useAgentUi.getState().set({ outgoing: { ...cur, files: cur.files.map((x) => (x.id === f.id ? { ...x, phase: 'failed', error: e instanceof Error ? e.message : String(e) } : x)) } });
            throw e;
          }
        }
        const turn = await api.agent.chat({ text, attachmentIds: ids, page, ...(st.conversationId ? { conversationId: st.conversationId } : {}), ...(opts.answer ? { answer: { messageId: opts.answer.messageId, choice: opts.answer.choice, skip: !!opts.answer.skip } } : {}) });
        qc.setQueryData<AgentThread>(qk.agent.thread, (old) => mergeTurn(old, turn));
        useAgentUi.getState().set({ conversationId: turn.conversationId, outgoing: null });
        if (turn.proposals.some((p) => p.status === 'applied')) await refreshData(qc);
        void qc.invalidateQueries({ queryKey: qk.agent.status });
        // the turn may have opened a case or changed what is missing; a new chat appears in the history
        void qc.invalidateQueries({ queryKey: ['agent', 'guide'] });
        void qc.invalidateQueries({ queryKey: qk.agent.conversations });
        return true;
      } catch (e) {
        // no Premium after all (trial used up in another tab, plan ended): the offer instead of an error, files and
        // text go back into the composer
        if (e instanceof ApiError && e.code === 'AGENT_PREMIUM_REQUIRED') {
          useAgentUi.getState().set({ blocker: 'premium', outgoing: null, attached: [...files, ...useAgentUi.getState().attached] });
          void qc.invalidateQueries({ queryKey: qk.agent.status });
          return false;
        }
        const cur = useAgentUi.getState().outgoing;
        useAgentUi.getState().set({ outgoing: cur ? { ...cur, failed: true } : null });
        throw e;
      } finally {
        useAgentUi.getState().set({ busy: false });
      }
    },
    [qc, status.data, page.path, page.propertyId, page.unitId, page.tenancyId],
  );

  /**
   * After "Übernehmen" of a proposal of this chat: the assistant goes on with the next step of the case by itself
   * (no "weiter" to type). Runs once per proposal; the server refuses a second time. No bubble, only the typing line.
   */
  const continueAfter = useCallback(
    async (p: AgentProposalDto) => {
      const st = useAgentUi.getState();
      if (st.busy || !p.conversationId || p.conversationId !== st.conversationId || p.status !== 'applied' || p.auto) return;
      st.set({ busy: true });
      try {
        const turn = await api.agent.chat({ conversationId: p.conversationId, continue: { proposalId: p.id }, page });
        qc.setQueryData<AgentThread>(qk.agent.thread, (old) => mergeTurn(old, turn));
        if (turn.proposals.some((x) => x.status === 'applied')) await refreshData(qc);
        void qc.invalidateQueries({ queryKey: qk.agent.status });
        void qc.invalidateQueries({ queryKey: ['agent', 'guide'] });
      } catch {
        // continuing is a convenience: the person can always type; the error of the card itself was shown already
      } finally {
        useAgentUi.getState().set({ busy: false });
      }
    },
    [qc, page.path, page.propertyId, page.unitId, page.tenancyId],
  );

  const newChat = useCallback(() => {
    writeHidden([]);
    useAgentUi.getState().set({ conversationId: null, outgoing: null, attached: [], blocker: null, hiddenTips: [] });
    qc.setQueryData<AgentThread>(qk.agent.thread, { conversation: null, messages: [], proposals: [] });
  }, [qc]);

  /** Continues an earlier chat from the history. */
  const openChat = useCallback(
    (id: string) => {
      if (useAgentUi.getState().busy) return;
      writeHidden([]);
      useAgentUi.getState().set({ conversationId: id, outgoing: null, blocker: null, hiddenTips: [] });
      void qc.resetQueries({ queryKey: qk.agent.thread });
    },
    [qc],
  );

  /** Called by the composer when files are added: without Premium the offer shows (files stay attached). */
  const checkAccess = useCallback(() => {
    const blocked = blockerNow();
    if (blocked) useAgentUi.getState().set({ blocker: blocked });
    return !blocked;
  }, [status.data]);

  return { ui, status, thread, tips, guide, ready, canSend, send, attach, newChat, openChat, checkAccess, continueAfter, page };
}

/** Earlier chats of this person, newest first, 20 at a time ("Mehr laden"). */
export function useAgentHistory(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: qk.agent.conversations,
    queryFn: ({ pageParam }) => api.agent.conversations({ limit: 20, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    staleTime: 60_000,
  });
}
